import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";

type ConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  icon: ReactNode;
  heading: string;
  subheading: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  isConfirming?: boolean;
  error?: string | null;
  /** Visual intent of the confirm button. Defaults to a destructive (error) action. */
  confirmColor?: "error" | "primary";
};

export function ConfirmationDialog({
  open,
  onClose,
  icon,
  heading,
  subheading,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  isConfirming = false,
  error = null,
  confirmColor = "error",
}: ConfirmationDialogProps) {
  const confirmBackground =
    confirmColor === "primary" ? "var(--theme-primary)" : "var(--theme-error)";
  const confirmHoverBackground =
    confirmColor === "primary" ? "var(--theme-hover-primary)" : "var(--theme-error)";
  const handleClose = (_event: object, reason?: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick" || isConfirming) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        root: {
          closeAfterTransition: false,
        },
        paper: {
          sx: {
            bgcolor: "var(--theme-surface)",
            backgroundImage: "none",
            border: "1px solid var(--theme-border)",
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ display: "flex", flexShrink: 0, pt: 0.25 }}>{icon}</Box>
          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 700,
                color: "var(--theme-text-primary)",
                lineHeight: 1.3,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {heading}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "var(--theme-text-secondary)",
                lineHeight: 1.5,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {subheading}
            </Typography>
            {error ? (
              <Alert
                severity="error"
                icon={<WarningAmber fontSize="inherit" />}
                sx={{
                  mt: 0.5,
                  bgcolor: "error.light",
                  color: "error.main",
                  alignItems: "center",
                  "& .MuiAlert-icon": {
                    color: "error.main",
                    opacity: 1,
                  },
                  "& .MuiAlert-message": {
                    color: "error.main",
                    fontWeight: 500,
                  },
                }}
              >
                {error}
              </Alert>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: "1px solid var(--theme-border)",
        }}
      >
        <Button
          variant="contained"
          onClick={onCancel}
          disabled={isConfirming}
          disableElevation
          sx={{
            textTransform: "none",
            bgcolor: "var(--theme-hover-tertiary)",
            color: "var(--theme-text-primary)",
            "&:hover": {
              bgcolor: "var(--theme-hover-secondary)",
            },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isConfirming}
          disableElevation
          sx={{
            textTransform: "none",
            bgcolor: confirmBackground,
            color: "var(--theme-text-on-primary)",
            "&:hover": {
              bgcolor: confirmHoverBackground,
            },
          }}
        >
          {isConfirming ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} color="inherit" />
              <span>{confirmLabel}</span>
            </Stack>
          ) : (
            confirmLabel
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
